import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createChart, CrosshairMode } from 'lightweight-charts';
import { baseChartOptions, getChartColors, tsToChartTime } from '../chartTheme';

function syncCharts(source, targets) {
    source.timeScale().subscribeVisibleLogicalRangeChange((range) => {
        if (!range) return;
        targets.forEach((chart) => {
            chart.timeScale().setVisibleLogicalRange(range);
        });
    });
}

/**
 * Linked lightweight-charts panes: OHLC + fills, OBI, optional equity.
 */
export default function ChartStack({
    candles = [],
    obiPoints = [],
    equityPoints = [],
    fills = [],
    isBookless = false,
    showEquity = false,
}) {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const colors = getChartColors(mode);

    const ohlcRef = useRef(null);
    const obiRef = useRef(null);
    const equityRef = useRef(null);
    const chartsRef = useRef([]);

    useEffect(() => {
        if (!ohlcRef.current || !candles.length) return undefined;

        const ohlcChart = createChart(ohlcRef.current, {
            ...baseChartOptions(mode, 300),
            crosshair: { mode: CrosshairMode.Normal },
        });
        const candleSeries = ohlcChart.addCandlestickSeries({
            upColor: colors.up,
            downColor: colors.down,
            borderUpColor: colors.up,
            borderDownColor: colors.down,
            wickUpColor: colors.up,
            wickDownColor: colors.down,
        });
        candleSeries.setData(
            candles.map((c) => ({
                time: tsToChartTime(c.ts),
                open: c.open,
                high: c.high,
                low: c.low,
                close: c.close,
            })),
        );

        if (fills.length) {
            candleSeries.setMarkers(
                fills.map((f) => ({
                    time: tsToChartTime(f.ts),
                    position: f.side === 'buy' ? 'belowBar' : 'aboveBar',
                    color: f.side === 'buy' ? colors.buy : colors.sell,
                    shape: f.side === 'buy' ? 'arrowUp' : 'arrowDown',
                    text: f.side.toUpperCase(),
                })),
            );
        }

        const charts = [ohlcChart];
        let obiChart = null;
        let equityChart = null;

        if (obiRef.current && obiPoints.length) {
            obiChart = createChart(obiRef.current, {
                ...baseChartOptions(mode, 140),
                crosshair: { mode: CrosshairMode.Normal },
            });
            const obiSeries = obiChart.addLineSeries({
                color: colors.obi,
                lineWidth: 2,
                priceLineVisible: false,
            });
            obiSeries.setData(
                obiPoints.map((p) => ({
                    time: tsToChartTime(p.ts),
                    value: p.value,
                })),
            );
            obiSeries.createPriceLine({
                price: 0,
                color: 'rgba(148,163,184,0.45)',
                lineWidth: 1,
                lineStyle: 2,
                axisLabelVisible: false,
            });
            charts.push(obiChart);
            syncCharts(ohlcChart, [obiChart]);
            syncCharts(obiChart, [ohlcChart]);
        }

        if (showEquity && equityRef.current && equityPoints.length) {
            equityChart = createChart(equityRef.current, {
                ...baseChartOptions(mode, 140),
                crosshair: { mode: CrosshairMode.Normal },
            });
            const eqSeries = equityChart.addAreaSeries({
                lineColor: colors.equity,
                topColor: `${colors.equity}44`,
                bottomColor: `${colors.equity}08`,
                lineWidth: 2,
                priceLineVisible: false,
            });
            eqSeries.setData(
                equityPoints.map((p) => ({
                    time: tsToChartTime(p.ts),
                    value: p.value,
                })),
            );
            charts.push(equityChart);
            syncCharts(ohlcChart, [equityChart]);
            syncCharts(equityChart, [ohlcChart]);
            if (obiChart) {
                syncCharts(obiChart, [equityChart]);
                syncCharts(equityChart, [obiChart]);
            }
        }

        chartsRef.current = charts;
        ohlcChart.timeScale().fitContent();

        return () => {
            charts.forEach((c) => c.remove());
            chartsRef.current = [];
        };
    }, [candles, obiPoints, equityPoints, fills, showEquity, mode]);

    if (!candles.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                No candles for this selection.
            </Typography>
        );
    }

    return (
        <Box>
            <Box ref={ohlcRef} sx={{ width: '100%' }} />
            {obiPoints.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Order Book Imbalance
                        {isBookless ? ' (flat — index)' : ''}
                    </Typography>
                    <Box ref={obiRef} sx={{ width: '100%' }} />
                </Box>
            )}
            {showEquity && equityPoints.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Equity curve
                    </Typography>
                    <Box ref={equityRef} sx={{ width: '100%' }} />
                </Box>
            )}
        </Box>
    );
}
